import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { attachStickySizePickerSync } from '@theme/sticky-size-picker-sync';
import {
  mediaQueryLarge,
  isMobileBreakpoint,
  getIOSVersion,
  fetchConfig,
  prefersReducedMotion,
} from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {number | undefined} */
  #resetTimeout;
  /** @type {(e: Event) => void} */
  #boundOutsideClick = () => {};
  /** @type {number | undefined} */
  #panelCloseTimer;
  /** @type {number} */
  #panelCloseId = 0;
  #boundVariantSelected = this.#updateQuickAddButtonState.bind(this);
  /** @type {import('./sticky-size-picker-sync').StickySizePickerSync | null} */
  #stickySync = null;

  get #isDropdownMode() {
    return this.dataset.mode === 'dropdown';
  }

  /** Sticky bar mobile picker — shares dropdown JS but has PDP sync + animated panel. */
  get #isStickyPicker() {
    return this.classList.contains('sticky-add-to-cart__picker');
  }

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#boundVariantSelected, {
      signal: this.#cartUpdateAbortController.signal,
    });

    if (this.#isDropdownMode) {
      this.#initDropdown();
    }

    if (this.#isStickyPicker) {
      this.#stickySync = attachStickySizePickerSync(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stickySync?.disconnect();
    this.#stickySync = null;

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();

    document.removeEventListener('click', this.#boundOutsideClick, true);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {import('./variant-picker').default} */ (
      modalContent.querySelector('variant-picker')
    );
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click — delegates to modal or dropdown based on mode
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isStickyPicker) {
      const ctaMode = this.dataset.stickyCtaMode || 'add';
      if (ctaMode === 'notify') {
        this.#stickySync?.openNotifyMeDialog();
        return;
      }
      if (ctaMode === 'sold-out') {
        return;
      }
    }

    if (this.#isDropdownMode) {
      this.#toggleSizesPanel();
      return;
    }

    const currentUrl = this.productPageUrl;

    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  /* ────────────────────────────────────────────────
   * Dropdown mode — inline sizes panel
   * ──────────────────────────────────────────────── */

  #initDropdown() {
    this.#boundOutsideClick = (e) => {
      if (this.#shouldIgnoreOutsideClick(e)) return;

      if (!this.contains(/** @type {Node} */ (e.target))) {
        this.#closeSizesPanel();
      }
    };

    for (const btn of this.querySelectorAll('[data-variant-id]')) {
      // Sticky sizes use delegated clicks — survives panel morph/animation.
      if (btn.closest('.sticky-add-to-cart__sizes')) continue;

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#handleSizeClick(e);
      });
    }

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const panel = this.#getSizesPanel();
        if (panel && this.#isSizesPanelOpen(panel)) {
          e.preventDefault();
          this.#closeSizesPanel();
          this.#getDisclosureTrigger()?.focus();
        }
      }
    });

    this.addEventListener('click', (e) => {
      const sizeButton =
        e.target instanceof Element ? e.target.closest('.sticky-add-to-cart__size[data-variant-id]') : null;
      if (sizeButton && this.contains(sizeButton)) {
        e.preventDefault();
        e.stopPropagation();
        this.#handleSizeClick(e);
        return;
      }

      e.stopPropagation();
    });
  }

  /**
   * Closes the inline sizes panel (sticky bar / dropdown trigger).
   * @param {Event} event
   */
  handleClosePanel = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.closeSizesPanel();
    this.#getDisclosureTrigger()?.focus();
  };

  /**
   * Opens/closes the sizes panel from a dedicated toggle button, bypassing the
   * notify/sold-out interception in `handleClick` — this button's only job is
   * letting the shopper see or change the size, regardless of CTA state.
   * @param {Event} event
   */
  handleToggleSizePanel = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#toggleSizesPanel();
  };

  /** Closes the sizes panel if open. Safe to call from parent components. */
  closeSizesPanel() {
    this.#closeSizesPanel();
  }

  /** @see StickySizePickerSync */
  syncSizeSelection() {
    this.#stickySync?.syncSizeSelection();
  }

  /** @see StickySizePickerSync */
  syncSizeRegionFromPdp() {
    this.#stickySync?.syncSizeRegionFromPdp();
  }

  /** @see StickySizePickerSync */
  syncCtaWithVariant() {
    this.#stickySync?.syncCtaWithVariant();
  }

  /**
   * Opens the size guide — local <size-guide> in the panel, or the PDP Sizebay widget.
   * @param {Event} event
   */
  handleSizeGuideClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const localGuide = this.querySelector('size-guide');
    if (localGuide instanceof HTMLElement) {
      localGuide.click();
      return;
    }

    const sizebayContainer = document.getElementById('sizebay-container');
    const trigger = sizebayContainer?.querySelector('button, a, [role="button"]');
    if (trigger instanceof HTMLElement) {
      trigger.click();
    }
  };

  /** @returns {HTMLElement | null} */
  #getSizesPanel() {
    const panel = this.querySelector('[data-sizes-panel]');
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * Region tabs (PDP or sticky) are outside the picker DOM but same product — keep panel open.
   * @param {Event} event
   */
  #shouldIgnoreOutsideClick(event) {
    if (!(event.target instanceof Element)) return false;

    const regionButton = event.target.closest('[data-action="switch-region"]');
    if (!regionButton) return false;

    const section = this.closest('.shopify-section');
    const clickSection = regionButton.closest('.shopify-section');

    return Boolean(section && clickSection === section);
  }

  /** @returns {HTMLButtonElement | null} */
  #getChooseTrigger() {
    const trigger = this.querySelector('.quick-add__button--choose');
    return trigger instanceof HTMLButtonElement ? trigger : null;
  }

  /**
   * The element that owns the listbox disclosure state (aria-expanded, focus-on-close).
   * Prefers a dedicated size-toggle button (sticky picker); falls back to the combined
   * add/choose trigger used everywhere else (collection quick-add has no separate toggle).
   * @returns {HTMLElement | null}
   */
  #getDisclosureTrigger() {
    const toggle = this.querySelector('.quick-add__button--size-toggle');
    return toggle instanceof HTMLElement ? toggle : this.#getChooseTrigger();
  }

  /** Sticky panel animates open/close; collection quick-add keeps instant hidden toggle.
   * @param {HTMLElement} panel
   */
  #panelUsesAnimatedToggle(panel) {
    return panel.classList.contains('sticky-add-to-cart__sizes');
  }

  /** @param {HTMLElement} panel */
  #isSizesPanelOpen(panel) {
    if (this.#panelUsesAnimatedToggle(panel)) {
      return !panel.hidden && panel.dataset.open === 'true';
    }

    return !panel.hidden;
  }

  /** @param {HTMLElement} panel */
  #finishPanelClose(panel) {
    if (this.#panelCloseTimer) {
      clearTimeout(this.#panelCloseTimer);
      this.#panelCloseTimer = undefined;
    }

    delete panel.dataset.open;
    panel.hidden = true;
  }

  /** @param {HTMLElement} panel */
  #cancelPanelCloseAnimation(panel) {
    this.#panelCloseId += 1;

    if (this.#panelCloseTimer) {
      clearTimeout(this.#panelCloseTimer);
      this.#panelCloseTimer = undefined;
    }

    // Drop in-flight transitionend handlers without re-binding per-button listeners.
    const inner = panel.querySelector('.sticky-add-to-cart__sizes-inner');
    if (inner instanceof HTMLElement) {
      inner.replaceWith(inner.cloneNode(true));
    }
  }

  #toggleSizesPanel() {
    const panel = this.#getSizesPanel();
    if (!panel) return;
    if (!this.#isSizesPanelOpen(panel)) {
      this.#openSizesPanel();
    } else {
      this.#closeSizesPanel();
    }
  }

  #openSizesPanel() {
    const panel = this.#getSizesPanel();
    const trigger = this.#getDisclosureTrigger();
    if (!panel || !trigger) return;

    if (this.#panelCloseTimer || panel.dataset.open !== 'true') {
      this.#cancelPanelCloseAnimation(panel);
    }

    panel.hidden = false;

    if (this.#panelUsesAnimatedToggle(panel) && !prefersReducedMotion()) {
      requestAnimationFrame(() => {
        panel.dataset.open = 'true';
      });
    } else if (this.#panelUsesAnimatedToggle(panel)) {
      panel.dataset.open = 'true';
    }

    trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.#boundOutsideClick, true);
  }

  #closeSizesPanel() {
    const panel = this.#getSizesPanel();
    const trigger = this.#getDisclosureTrigger();
    if (!panel || !trigger) return;

    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.#boundOutsideClick, true);

    if (this.#panelUsesAnimatedToggle(panel) && panel.dataset.open === 'true' && !prefersReducedMotion()) {
      delete panel.dataset.open;

      const closeId = this.#panelCloseId + 1;
      this.#panelCloseId = closeId;

      const inner = panel.querySelector('.sticky-add-to-cart__sizes-inner');
      const finish = () => {
        if (closeId !== this.#panelCloseId) return;

        inner?.removeEventListener('transitionend', onEnd);
        this.#finishPanelClose(panel);
      };

      /** @param {TransitionEvent} e */
      const onEnd = (e) => {
        if (e.target === inner && (e.propertyName === 'opacity' || e.propertyName === 'transform')) {
          finish();
        }
      };

      if (inner instanceof HTMLElement) {
        inner.addEventListener('transitionend', onEnd);
      }

      this.#panelCloseTimer = window.setTimeout(finish, 450);
      return;
    }

    this.#finishPanelClose(panel);
  }

  /** @param {Event} e */
  async #handleSizeClick(e) {
    const button =
      e.currentTarget instanceof HTMLButtonElement && e.currentTarget.dataset.variantId
        ? e.currentTarget
        : e.target instanceof Element
          ? e.target.closest('[data-variant-id]')
          : null;

    if (!(button instanceof HTMLButtonElement)) return;

    const variantId = button.dataset.variantId;
    const isAvailable = button.dataset.available === 'true';

    if (!variantId) return;

    if (this.#isStickyPicker && !isAvailable) {
      this.#stickySync?.setSizeSelection(variantId);

      if (button.dataset.bisEnabled === 'true') {
        this.#stickySync?.syncPdpSizeSelection(variantId);
        this.#stickySync?.setCtaMode('notify', variantId, button);
      } else {
        this.#stickySync?.syncPdpSizeSelection(variantId);
        this.#stickySync?.setCtaMode('sold-out');
      }

      this.#closeSizesPanel();
      return;
    }

    if (!isAvailable) return;

    if (this.#isStickyPicker) {
      this.#stickySync?.setCtaMode('add');
    }

    this.#closeSizesPanel();
    this.#setDropdownLoading(true);

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      // Attribute the add-to-cart to a Sizebay recommendation when available.
      if (typeof (/** @type {any} */ (window).getSizebaySessionIdV4) === 'function') {
        const sizebaySessionId = /** @type {any} */ (window).getSizebaySessionIdV4();
        if (sizebaySessionId) formData.append('attributes[SIZEBAY_SESSION_ID_V4]', sizebaySessionId);
      }

      const sectionsToRender = getSectionsToRender();
      if (sectionsToRender.length > 0) {
        formData.append('sections', sectionsToRender.join(','));
      }

      const cartAddUrl = /** @type {any} */ (window).Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        ...fetchConfig('javascript', { body: formData }),
        headers: { Accept: 'application/json' },
      });

      const data = await response.json();

      if (data.status) {
        this.#setDropdownLoading(false);
        this.#announceStatus(data.message || this.dataset.errorText || 'Error adding to cart');
        return;
      }

      this.#setDropdownLoading(false);
      this.#animateDropdownAdded();
      this.#announceStatus(this.dataset.addedText || 'Added to cart');

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'quick-add',
          itemCount: 1,
          sections: data.sections,
        }),
      );
    } catch {
      this.#setDropdownLoading(false);
      this.#announceStatus(this.dataset.errorText || 'Error adding to cart');
    }
  }

  /** @param {boolean} loading */
  #setDropdownLoading(loading) {
    const trigger = this.#getChooseTrigger();
    if (!trigger) return;

    if (loading) {
      this.dataset.state = 'loading';
      trigger.disabled = true;
    } else {
      delete this.dataset.state;
      trigger.disabled = false;
    }
  }

  #animateDropdownAdded() {
    const trigger = this.#getChooseTrigger();
    if (!trigger) return;

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    trigger.dataset.added = 'true';

    this.#resetTimeout = window.setTimeout(() => {
      trigger.removeAttribute('data-added');
    }, 2000);
  }

  /** @param {string} message */
  #announceStatus(message) {
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = message;
  }

  /* ────────────────────────────────────────────────
   * Modal mode
   * ──────────────────────────────────────────────── */

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {import('./events').VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

/** @returns {string[]} */
const getSectionsToRender = () => {
  /** @type {string[]} */
  const sections = [];
  for (const element of document.querySelectorAll('[data-section-id]')) {
    const sectionId = element.getAttribute('data-section-id');
    if (sectionId && !sections.includes(sectionId)) {
      sections.push(sectionId);
    }
  }
  return sections;
};

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {import('./events').CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
